import { useCallback, useEffect, useState } from 'react'
import { authedAxios } from '../../utils'

const DEFAULT_CUSTOMER = {
  name: '',
  typeId: 1,
  address: '',
  website: '',
  primaryPhone: '',
  primaryEmail: '',
  customFields: {},
  leadSourceTypeId: '',
  industryTypeId: '',
  importanceTypeId: 1,
  accountOwners: [],
  faxNumber: '',
}

const useCustomer = (id, { skip } = {}) => {
  const [customer, setCustomer] = useState(DEFAULT_CUSTOMER)
  const [customerCopy, setCustomerCopy] = useState(DEFAULT_CUSTOMER)

  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)

  const isNew = id === 'new'

  const getCustomer = useCallback(
    (showLoadingAnimation = true) => {
      if (!id || skip) return
      {
        Promise.resolve()
      }

      if (isNew) {
        setCustomer(DEFAULT_CUSTOMER)
        setIsLoading(false)
        return Promise.resolve()
      }

      setIsLoading(showLoadingAnimation)
      return authedAxios
        .get(`/api/customer/${id}`)
        .then((res) => {
          setCustomer(res.data.customer)
          setCustomerCopy(JSON.parse(JSON.stringify(res.data.customer)))
          setIsLoading(false)
          return res
        })
        .catch((e) => {
          setIsLoading(false)
          setHasLoadError(true)
        })
    },
    [id, skip, isNew]
  )

  const reset = ({ resetCustomFields, resetCustomerInfo }) => {
    let newValue = JSON.parse(JSON.stringify(customer))

    if (resetCustomFields) {
      newValue.customFields = customerCopy.customFields
    }

    if (resetCustomerInfo) {
      newValue = {
        ...customerCopy,
        customFields: newValue.customFields,
      }
    }

    setCustomer(JSON.parse(JSON.stringify(newValue)))
  }

  const saveCustomer = ({
    saveCustomFields,
    saveCustomerInfo,
    currentOpportunity,
    allowDuplicateName,
  }) => {
    // This is a quick hack to save custom fields and info separately
    // TECH-DEBT: separate custom fields into their own entity with its own endpoints
    let body = JSON.parse(JSON.stringify(customerCopy))

    if (saveCustomFields) {
      body.customFields = customer.customFields
    }

    if (saveCustomerInfo) {
      body = {
        ...customer,
        customFields: body.customFields,
      }
    }

    if (currentOpportunity?.isOpen) {
      body.productOfInterest = currentOpportunity.products
        .map((p) => p.name)
        .join(', ')
    }

    return (
      isNew
        ? authedAxios.post(
            `/api/customer?allow_duplicate_name=${allowDuplicateName}`,
            body
          )
        : authedAxios.put('/api/customer', body)
    ).then((res) => {
      setCustomerCopy(body)
      return res
    })
  }

  const archiveCustomer = () => {
    return authedAxios.delete(`/api/customer/${customer.id || id}`)
  }

  useEffect(() => {
    getCustomer()
  }, [id, skip])

  return {
    customer,
    setCustomer,
    isLoading,
    hasLoadError,
    getCustomer,
    reset,
    saveCustomer,
    archiveCustomer,
  }
}

export default useCustomer
